import { uploadData } from "aws-amplify/storage";
import { Amplify } from "aws-amplify";
import config from "../config";
import { rekognition as handleRekognition } from "@nualang/nualang-api-and-queries/APIs/Services";

import { fileTypeFromBuffer } from "file-type";

const { ImageURL, ImageBucket } = config;
const albumBucketName = ImageBucket;

const urlExpression =
  /[-a-zA-Z0-9@:%_+.~#?&//=]{2,256}\.[a-z]{2,4}\b(\/[-a-zA-Z0-9@:%_.~#?&//=]*)?/gi;
const urlRegex = new RegExp(urlExpression);

const isUrl = (image) => image.match(urlRegex);
const MINIMUM_SIZE = 300000;
const DEFAULT_WIDTH = 1280;

function GetImageSize(image) {
  const sizeInBytes = 4 * Math.ceil(image.length / 3) * 0.5624896334383812;
  return sizeInBytes;
}

function getImageDimensions(file) {
  return new Promise((resolved, rejected) => {
    const i = new Image();
    i.onload = function () {
      resolved({ w: i.width, h: i.height });
    };
    i.onerror = function () {
      rejected(new Error("There was a problem with the image."));
    };
    i.src = file;
  });
}

function IsLargeImage(dimensions, imageSize) {
  return dimensions.w > DEFAULT_WIDTH || imageSize > MINIMUM_SIZE;
}

function ApplyEdits(folder, photoKey, dimensions, imageSize) {
  if (
    [
      "courses",
      "classrooms",
      "phrases",
      "roleplays",
      "bots",
      "topics",
    ].includes(folder) &&
    IsLargeImage(dimensions, imageSize)
  ) {
    const imageRequestParams = {
      bucket: albumBucketName,
      key: photoKey,
      edits: {
        resize: {
          width: DEFAULT_WIDTH,
          fit: "cover",
        },
      },
    };
    const imageRequest = JSON.stringify(imageRequestParams);
    const url = `${ImageURL}/${Buffer.from(imageRequest).toString("base64")}`;
    return url;
  }
  if (folder.startsWith("bots/") && IsLargeImage(dimensions, imageSize)) {
    const imageRequestParams = {
      bucket: albumBucketName,
      key: photoKey,
      edits: {
        resize: {
          width: DEFAULT_WIDTH,
          fit: "cover",
        },
      },
    };
    const imageRequest = JSON.stringify(imageRequestParams);
    const url = `${ImageURL}/${Buffer.from(imageRequest).toString("base64")}`;
    return url;
  }
  if (["users"].includes(folder)) {
    const imageRequestParams = {
      bucket: albumBucketName,
      key: photoKey,
      edits: {
        resize: {
          width: 200,
          height: 200,
          fit: "cover",
        },
      },
    };
    const imageRequest = JSON.stringify(imageRequestParams);
    const url = `${ImageURL}/${Buffer.from(imageRequest).toString("base64")}`;
    return url;
  }
  const imageRequest = JSON.stringify({
    bucket: albumBucketName,
    key: photoKey,
  });
  const url = `${ImageURL}/${Buffer.from(imageRequest).toString("base64")}`;
  return url;
}

function stripBase64Prefix(base64String) {
  return base64String.replace(/^data:image\/[a-zA-Z]+;base64,/, "");
}

async function UploadImage(image, filename, folder) {
  // Check if the image is a URL and skip upload if it is
  if (isUrl(image)) {
    return [image, ""];
  }
  const base64Data = image.startsWith("data:image/")
    ? stripBase64Prefix(image)
    : image;
  const fileName = `${filename}-${Date.now()}`;
  const buffer = Buffer.from(base64Data, "base64");
  const fileMime = await fileTypeFromBuffer(buffer);
  if (!fileMime) {
    throw new Error("Could not determine the file type from the buffer.");
  }

  const photoKey = `${folder}/${fileName}.${fileMime.ext}`;
  const tempImageSrc = `data:image/${fileMime.ext};charset=utf-8;base64,${base64Data}`;
  const dimensions = await getImageDimensions(tempImageSrc);
  const imageSize = GetImageSize(base64Data);

  Amplify.configure({
    ...Amplify.getConfig(),
    Storage: {
      S3: {
        region: process.env.REACT_APP_REGION,
        bucket: ImageBucket,
      },
    },
  });

  await uploadData({
    path: photoKey,
    data: buffer,
    options: {
      contentType: fileMime.mime,
    },
  }).result;

  let moderated = false;
  let warn = false;
  let moderatedLabels = null;
  const checkModeration = await handleRekognition({ Name: photoKey });
  if (
    checkModeration.ModerateImage === true &&
    !checkModeration.onlyAllowedLabels
  ) {
    moderated = true;
    warn = null;
    moderatedLabels = checkModeration.ModerationLabels.map(
      (label) => label.Name,
    );
  } else if (
    checkModeration.ModerateImage &&
    checkModeration.onlyAllowedLabels
  ) {
    moderated = true;
    warn = true;
    moderatedLabels = checkModeration.ModerationLabels.map(
      (label) => label.Name,
    );
  }
  const url = ApplyEdits(
    folder,
    photoKey,
    dimensions,
    imageSize,
    checkModeration,
  );
  return [url, photoKey, moderated, warn, moderatedLabels];
}

export async function UploadUserImage(image, filename) {
  const folder = "users";
  if (!image) {
    return alert("Please choose a file to upload first.");
  }
  const response = await UploadImage(image, filename, folder);
  return response;
}

export async function UploadCourseImage(image, filename) {
  const folder = "courses";
  if (!image) {
    return alert("Please choose a file to upload first.");
  }
  const response = await UploadImage(image, filename, folder);
  return response;
}

export async function UploadClassroomImage(image, filename) {
  const folder = "classrooms";
  if (!image) {
    return alert("Please choose a file to upload first.");
  }
  const response = await UploadImage(image, filename, folder);
  return response;
}

export async function UploadPhraseImage(image, filename) {
  const folder = "phrases";
  if (!image) {
    return alert("Please choose a file to upload first.");
  }
  const response = await UploadImage(image, filename, folder);
  return response;
}

export async function UploadPhraseListImage(image, filename) {
  const folder = "phrase-lists";
  if (!image) return alert("Please choose a file to upload first.");
  const response = await UploadImage(image, filename, folder);
  return response;
}

export async function UploadRoleplayImage(image, filename) {
  const folder = "roleplays";
  if (!image) {
    return alert("Please choose a file to upload first.");
  }
  const response = await UploadImage(image, filename, folder);
  return response;
}

export async function UploadBotImage(image, filename) {
  const folder = "bots";
  if (!image) {
    return alert("Please choose a file to upload first.");
  }
  const response = await UploadImage(image, filename, folder);
  return response;
}

export async function UploadBotExerciseImage(botId, image, filename) {
  const folder = `bots/${botId}`;
  if (!image) {
    return alert("Please choose a file to upload first.");
  }
  const response = await UploadImage(image, filename, folder);
  return response;
}

export async function UploadTopicImage(image, filename) {
  const folder = "topics";
  if (!image) {
    return alert("Please choose a file to upload first.");
  }
  const response = await UploadImage(image, filename, folder);
  return response;
}

export async function UploadReportProblemImage(image, filename) {
  const folder = "reportProblem";
  const response = await UploadImage(image, filename, folder);
  return response[0];
}
